import React, { Component } from "react";

// Third - Party
import i18n from "./i18n";

// Utils
import {
  resolveUnitPreferences,
  resolveDisplayCurrency
} from "utils/units/preferences";
import { resolveUiLocale } from "utils/uiLocale";

// Other
import { getProfile } from "services/users";
import {
  getAuthToken,
  T_SUPERUSER,
  T_ADMIN,
  T_USER,
  T_VIEWER,
  T_DESIGNER
} from "assets/global";

const AuthContext = React.createContext();
export { AuthContext };

/*
React context provider with the functions to test if the user is or has been logged in the application
*/
class AuthProvider extends Component {
  constructor() {
    super();
    this.state = {
      isAuth: false,
      loading: true,
      isChatOnline: false,
      isChatLoaded: false,
      isSuperAdminAccessConfirmed: false,
      chatVisibility: "hidden", // 'maximized' | 'minimized' | 'hidden'
      userName: "",
      userType: "",
      companyId: null,
      salesData: null,
      tfaEnabled: false,
      id: null,
      hmacCode: null,
      unitSystem: "metric",
      numberLocale: "en-US",
      currency: "EUR"
    };
  }

  componentDidMount() {
    this.authenticate();
  }

  authenticate = token => {
    /*
    Check if there is a appToken in the localStorage
    - This should be change to store the appToken in a more secure space

    @ param - no params
    @ return - change in isAuth state
    */
    let appToken = token || getAuthToken();
    if (appToken) {
      // app.defaults.headers.Authorization = `Token ${appToken}`;

      const storedLocale = localStorage.getItem("user_locale");
      if (storedLocale !== null) {
        const locale = resolveUiLocale(storedLocale) || "en";
        i18n.changeLanguage(locale);
        localStorage.setItem("user_locale", locale);
      }

      return getProfile()
        .then(data => {
          // Requests aborted by a page reload (e.g. the login/reload
          // sequence in Login.jsx) resolve with status 0 and an
          // empty body instead of rejecting, so the profile can be missing.
          const profile = Array.isArray(data) ? data[0] : undefined;
          if (!profile) {
            this.setState({
              isAuth: false,
              loading: false,
              isSuperAdminAccessConfirmed: false
            });
            return;
          }

          const unitPreferences = resolveUnitPreferences(profile);
          const currency = resolveDisplayCurrency(profile);
          const locale = resolveUiLocale(profile.locale) || "en";
          i18n.changeLanguage(locale);

          let utype = T_USER;
          switch (profile.user_type) {
            case "ADMIN":
              utype = T_ADMIN;
              break;
            case "ROOT":
              utype = T_SUPERUSER;
              break;
            case "VIEWER":
              utype = T_VIEWER;
              break;
            case "DESIGNER":
              utype = T_DESIGNER;
              break;
            default:
              utype = T_USER;
          }

          this.setState({
            isAuth: true,
            userType: utype,
            isSuperAdminAccessConfirmed: false,
            loading: false,
            userName: profile.username,
            tfaEnabled: profile.tfa_enabled,
            profileId: profile.id,
            companyId: profile.company,
            salesData: profile.sales_data,
            hmacCode: profile.hmac_code,
            unitSystem: unitPreferences.system,
            numberLocale: unitPreferences.locale,
            currency
          });

          localStorage.setItem("user_locale", locale);

          // Authentication worked, so release the one-shot auto-login guard
          // Login.jsx sets, keeping it available for a future failed boot.
          sessionStorage.removeItem("autoLoginAttempted");
        })
        .catch(err => {
          this.setState({
            isAuth: false,
            loading: false,
            isSuperAdminAccessConfirmed: false
          });
          throw err;
        });
    } else {
      this.setState({ loading: false });
      return Promise.resolve();
    }
  };
  logout = () => {
    /*
    Set method to log out, clears the localStorage and sets isAuth to false

    @ param - no params
    @ return - change in isAuth state
    */
    const user_locale = localStorage.getItem("user_locale");
    const cookieConsent = localStorage.getItem("CookieConsent");

    localStorage.clear();
    localStorage.setItem("user_locale", user_locale);
    localStorage.setItem("CookieConsent", cookieConsent);
    sessionStorage.clear();

    this.setState({
      isAuth: false,
      userType: false,
      isSuperAdminAccessConfirmed: false
    });

    // window.location.reload()
  };

  setChatState = state => {
    if (state === "offline") {
      this.setState({ isChatOnline: false });
    } else {
      this.setState({ isChatOnline: true });
    }
  };

  setChatLoaded = state => {
    this.setState({ isChatLoaded: state });
  };

  setChatVisibility = state => {
    switch (state) {
      case "minimized":
        this.setState({ chatVisibility: "minimized" });
        break;
      case "maximized":
        this.setState({ chatVisibility: "maximized" });
        break;
      case "hidden":
      default:
        this.setState({ chatVisibility: "hidden" });
        break;
    }
  };

  setTfaEnabled = state => {
    this.setState({ tfaEnabled: state });
  };

  /**
   * Updates normalized display preferences in context from profile-shaped fields.
   * `display_currency` is display-only (symbol/abbreviation in cost views);
   * amounts are not converted.
   * @param {{ unit_system?: string, number_locale?: string, display_currency?: string }} prefs
   */
  setUnitPreferences = prefs => {
    const { system, locale } = resolveUnitPreferences(prefs || {});
    const currency = resolveDisplayCurrency(prefs || {});
    this.setState({
      unitSystem: system,
      numberLocale: locale,
      currency
    });
  };

  /**
   * Persists an allowed UI locale and switches i18n without re-authenticating.
   * Invalid or unsafe locales reject before storage or i18n is updated.
   * @param {string} locale
   * @param {string[]} [extraLocales=[]] Additional allowed locale codes.
   * @returns {Promise<void>} Resolves when `i18n.changeLanguage` completes (or rejects if invalid / i18n fails).
   */
  setLocalePreference = (locale, extraLocales = []) => {
    const resolvedLocale = resolveUiLocale(locale, extraLocales);

    if (!resolvedLocale) {
      return Promise.reject(new Error("Invalid locale"));
    }

    localStorage.setItem("user_locale", resolvedLocale);
    return Promise.resolve(i18n.changeLanguage(resolvedLocale));
  };

  /**
   * Function to change the confirmation status of the super admin access.
   * If it is false some features will be disabled, for example, showing the projects of the account.
   * Since it only applies for the super admin, if an user with another role tries to call this function,
   * it will just return
   * @param {boolean} state - It can be true (access confirmed) or false (access denied)
   */
  setSuperAdminAccessConfirmationStatus = state => {
    if (this.state.userType !== T_SUPERUSER) return;

    this.setState({
      isSuperAdminAccessConfirmed: state
    });
  };

  render() {
    return (
      <AuthContext.Provider
        value={{
          isAuthenticated: this.state.isAuth, // variable that shows if the user is authenticated or not
          userType: this.state.userType, // which kind of user logged in
          login: this.authenticate, // set method to set isAuth=True
          logout: this.logout, // set method to set isAuth=True
          loading: this.state.loading,
          chatVisibility: this.state.chatVisibility,
          setChatVisibility: this.setChatVisibility,
          setChatState: this.setChatState,
          isChatOnline: this.state.isChatOnline,
          setChatLoaded: this.setChatLoaded,
          isChatLoaded: this.state.isChatLoaded,
          userName: this.state.userName,
          tfaEnabled: this.state.tfaEnabled,
          profileId: this.state.profileId,
          companyId: this.state.companyId,
          salesData: this.state.salesData,
          hmacCode: this.state.hmacCode,
          unitSystem: this.state.unitSystem,
          numberLocale: this.state.numberLocale,
          currency: this.state.currency,
          setUnitPreferences: this.setUnitPreferences,
          setLocalePreference: this.setLocalePreference,
          setTfaEnabled: this.setTfaEnabled,
          isSuperAdminAccessConfirmed: this.state.isSuperAdminAccessConfirmed,
          setSuperAdminAccessConfirmationStatus:
            this.setSuperAdminAccessConfirmationStatus
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}
let AuthConsumer = AuthContext.Consumer;
export { AuthConsumer };
export default AuthProvider;
